import {
  transformUrnToWitboostId,
  WitboostDomain,
} from '@agilelab/plugin-wb-builder-common';
import {
  GenericElement,
  NotFoundException,
  Pagination,
  SearchFilter,
  TableCellProps,
  WbResponsiveTableToolbar,
} from '@agilelab/plugin-wb-platform';
import { parseEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { Box, Typography } from '@material-ui/core';
import { FC, ReactNode, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../scopeProvider.factory';
import {
  DomainFilters,
  MyProvider,
  ProviderFilters,
  ScopeProviderType,
} from '../types';
import { getLabelAndValue } from '../../utils';

export type DomainTypeEntity = GenericElement & WitboostDomain;

interface DomainContextType
  extends ScopeProviderType<GenericElement, ProviderFilters> {}

const [useDomainContext, DomainContextProvider] =
  contextProviderFactory<DomainContextType>();

export default useDomainContext;

export interface DomainProviderProps {
  children: ReactNode;
}

export const DomainFiltersSection = () => {
  const { filters, changeFilters, resetFilters } = useDomainContext();

  return (
    <WbResponsiveTableToolbar
      onClear={resetFilters}
      style={{ marginTop: '16px' }}
      marginBottom
    >
      <WbResponsiveTableToolbar.Filters>
        <SearchFilter
          key="search"
          value={filters.search ?? ''}
          changeFilters={changeFilters}
        />
      </WbResponsiveTableToolbar.Filters>
    </WbResponsiveTableToolbar>
  );
};

export const DomainProvider: FC<DomainProviderProps> = props => {
  const { children } = props;
  const catalogApi = useApi(catalogApiRef);
  const [filters, setFilters] = useState<DomainFilters>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 25,
    currentPage: 0,
  });
  const defaultColumns: TableCellProps<GenericElement>[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'name',
        fieldRender: (field: GenericElement) => {
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{field.displayName}</Typography>
            </Box>
          );
        },
      },
      {
        headerName: 'Description',
        field: 'description',
        fieldRender: (field: GenericElement) => {
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{field.description}</Typography>
            </Box>
          );
        },
      },
    ];
  }, []);

  const systemState = useAsync(async () => {
    const result = await catalogApi.queryEntities({
      filter: {
        kind: ['Domain'],
      },
      limit: pagination.limit,
      cursor: pagination.cursor,
      fullTextFilter: {
        term: filters.search!,
        fields: ['spec.mesh.name', 'metadata.name'],
      },
    });
    setNextCursor(result.pageInfo.nextCursor);
    setPrevCursor(result.pageInfo.prevCursor);
    setCount(result.totalItems);
    return result.items.map(entity => getLabelAndValue(entity));
  }, [filters, pagination.limit, pagination.cursor]);

  const getRowId = (row: any) => row.id;

  return (
    <DomainContextProvider
      value={
        {
          entitiesState: systemState,
          columns: defaultColumns,
          getRowId,
          filters,
          changeFilters: <K extends keyof DomainFilters>(
            key: K,
            filterValue: DomainFilters[K],
          ) => {
            setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
            setFilters(f => ({ ...f, [key]: filterValue }));
          },
          resetFilters: () => {
            setFilters({});
          },
          pagination,
          setPagination,
          count,
          nextCursor,
          prevCursor,
        } as unknown as ScopeProviderType<GenericElement, ProviderFilters>
      }
    >
      {children}
    </DomainContextProvider>
  );
};

export class DomainHandlerProvider
  implements MyProvider<GenericElement, ProviderFilters>
{
  private readonly catalogApi: CatalogApi;

  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  useContext = useDomainContext;

  renderContextProvider(children: ReactNode) {
    return <DomainProvider>{children}</DomainProvider>;
  }

  filtersSection = () => <DomainFiltersSection />;

  async resolve(ref: string) {
    const resolvedRef = transformUrnToWitboostId(ref) ?? ref;
    const parsedEntityRef = parseEntityRef(resolvedRef);
    try {
      if (parsedEntityRef.kind === 'domain') {
        const entity = await this.catalogApi.getEntityByRef(resolvedRef);
        if (entity) return getLabelAndValue(entity);
        throw new NotFoundException(
          `Cannot find a Domain entity with ref: ${ref}`,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving entity with ref: ${ref}`);
    }
  }
}
